import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Referral } from '@/types';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ReferralStageBadge, REFERRAL_STAGE_LABELS } from '@/components/ReferralStageBadge';
import { formatDate, formatDateOnly, formatCurrency, cn } from '@/lib/utils';
import { ArrowLeft, Loader2 } from 'lucide-react';

interface Step {
  label: string;
  date: string | null;
  done: boolean;
}

function buildTimeline(r: Referral): Step[] {
  const steps: Step[] = [{ label: 'Referred to Abby', date: r.referred_on, done: true }];
  if (r.stage === 'lost') {
    steps.push({ label: 'Did not sign up', date: null, done: true });
    return steps;
  }
  if (r.stage === 'lead') {
    steps.push({ label: 'Signed up', date: null, done: false });
    return steps;
  }
  steps.push({ label: 'Signed up', date: r.signed_up_on, done: true });
  if (r.stage === 'closed' && !r.contract_start) {
    steps.push({ label: 'Account closed', date: r.contract_end, done: true });
    return steps;
  }
  steps.push({ label: 'Paid service started', date: r.contract_start, done: !!r.contract_start || r.stage === 'paying' });
  steps.push(
    r.stage === 'closed'
      ? { label: 'Account closed', date: r.contract_end, done: true }
      : { label: 'Still active', date: null, done: r.stage === 'paying' || r.stage === 'trial' }
  );
  return steps;
}

export function ReferralDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [referral, setReferral] = useState<Referral | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    setReferral(null);
    api
      .getReferral(id)
      .then(({ referral }) => setReferral(referral))
      .catch((err: { error?: string }) => setError(err.error || 'Referral not found'))
      .finally(() => setIsLoading(false));
  }, [id]);

  return (
    <Layout>
      <div className="space-y-6">
        <Link to="/" className="inline-flex items-center text-sm text-abby-primary hover:underline">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to referrals
        </Link>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-abby-primary" />
          </div>
        ) : error || !referral ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-600">{error || 'Referral not found'}</CardContent>
          </Card>
        ) : (
          <>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{referral.name}</h1>
                <p className="text-gray-600 mt-1">{referral.stage_label || REFERRAL_STAGE_LABELS[referral.stage]}</p>
              </div>
              <ReferralStageBadge stage={referral.stage} label={referral.stage_label} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-4">
                    {buildTimeline(referral).map((step, i) => (
                      <li key={i} className="flex gap-3">
                        <span
                          className={cn(
                            'mt-1.5 h-2.5 w-2.5 rounded-full flex-shrink-0',
                            step.done ? 'bg-abby-primary' : 'bg-gray-300'
                          )}
                        />
                        <div>
                          <p className={cn('text-sm', step.done ? 'text-gray-900' : 'text-gray-400')}>{step.label}</p>
                          <p className="text-xs text-gray-500">
                            {step.date ? formatDateOnly(step.date) : step.done ? 'date not recorded' : 'not yet'}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Details</CardTitle>
                  <CardDescription>Business name, status and billing shown. Contact details stay with Abby.</CardDescription>
                </CardHeader>
                <CardContent>
                  <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
                    <dt className="text-gray-500">Latest monthly</dt>
                    <dd className="text-gray-900 tabular-nums">
                      {referral.latest_monthly_amount != null
                        ? `${formatCurrency(referral.latest_monthly_amount)}${
                            referral.latest_monthly_date ? ` (${formatDateOnly(referral.latest_monthly_date)})` : ''
                          }`
                        : '-'}
                    </dd>
                    <dt className="text-gray-500">Source</dt>
                    <dd className="text-gray-900">{referral.lead_source || '-'}</dd>
                    <dt className="text-gray-500">Last updated</dt>
                    <dd className="text-gray-900">
                      {referral.last_synced_at ? formatDate(referral.last_synced_at) : '-'}
                    </dd>
                  </dl>
                  <div className="mt-4">
                    <Link to="/toolkit">
                      <Button variant="outline" size="sm">
                        Referral Toolkit
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
